import { Box, TextField, InputAdornment, Typography, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import Close from '@mui/icons-material/Close';
import { useTranslations } from '@/i18n/use-translations';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import PageHeaderBar from '@/components/common/page-header-bar';
import type { ReactNode } from 'react';

interface TopBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  title: string;
  subtitle?: ReactNode;
  avatar?: ReactNode;
  action?: ReactNode;
  showSearch?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const TopBar = ({
  searchTerm,
  onSearchChange,
  title,
  subtitle,
  avatar,
  action,
  showSearch = true,
  showBackButton = false,
  onBack,
}: TopBarProps) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchExpanded = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
    });
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchCollapse = () => {
    setIsSearchExpanded(false);
    onSearchChange('');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSearchCollapse();
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/contributions');
    }
  };

  const searchContent = (
    <TextField
      variant="outlined"
      placeholder={t('common.search')}
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      onKeyDown={handleSearchKeyDown}
      fullWidth
      size="small"
      inputRef={searchInputRef}
      sx={{
        '& .MuiOutlinedInput-root': {
          'borderRadius': 2,
          'backgroundColor': 'background.default',
          'transition': 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-focused': {
            backgroundColor: 'background.paper',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
          },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearchCollapse} size="small" sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const mainContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pt: 1,
        width: '100%',
        minWidth: 0,
        pl: showBackButton ? 0 : 2,
      }}
    >
      {/* Back button */}
      {showBackButton && (
        <IconButton onClick={handleBack} size="small" sx={{ color: 'text.primary', flexShrink: 0 }}>
          <ArrowBackIosNew sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {/* Card header: Avatar, Title, Subtitle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        {avatar && <Box sx={{ flexShrink: 0 }}>{avatar}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
            }}
          >
            {title}
          </Typography>
          {subtitle &&
            (typeof subtitle === 'string' ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                {subtitle}
              </Typography>
            ) : (
              <Box sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 0 }}>
                {subtitle}
              </Box>
            ))}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Box>

      {/* Search */}
      {showSearch && (
        <>
          {/* Mobile: search icon button */}
          <IconButton
            onClick={handleSearchExpand}
            sx={{
              color: 'text.secondary',
              display: { xs: 'inline-flex', lg: 'none' },
              flexShrink: 0,
            }}
          >
            <SearchIcon />
          </IconButton>

          {/* Desktop: persistent search field */}
          <Box sx={{ display: { xs: 'none', lg: 'block' }, width: 300, flexShrink: 0 }}>
            <TextField
              variant="outlined"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  'borderRadius': 2,
                  'backgroundColor': 'background.default',
                  'transition': 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'background.paper',
                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <PageHeaderBar
      searchExpanded={isSearchExpanded}
      searchContent={searchContent}
      mainContent={mainContent}
      onSearchExpanded={handleSearchExpanded}
      disableBorder
    />
  );
};

export default TopBar;
